import React, { useContext, useState, useEffect, useRef } from "react"
import { useCosmicSchool } from "../data/useCosmicSchool"
import { useSQLPetersonsSchool } from "../data/useSQLPetersonsSchool"
import { navigate } from "../link"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SEO from "../components/seo/seo"
import Bookmark from "../components/bookmark/bookmark"
import {
  cosmicStudentStoryToStoryCardContent,
  cosmicStoryToStoryCardContent,
} from "../components/dashboard/prospective-student-tool/StoryCard.js"
import SectionMajorsDegrees from "../components/schools/schools-majors-degrees"
import SectionAdmissions from "../components/schools/schools-admissions"
import SectionTuitionAid from "../components/schools/schools-tuition-aid"
import SectionStudentBody from "../components/schools/schools-student-body"
import SectionAthletics from "../components/schools/schools-athletics"
import { StoryCardCarousel } from "../components/dashboard/prospective-student-tool/StoryCardCarousel.js"
import FilterableCardGrid from "../components/generic/FilterableCardGrid"
import { FirebaseContext } from "../components/Firebase"
import Fuse from "fuse.js"

import { useIntl, FormattedMessage } from "react-intl"
import { useAlumniStories } from "../components/hooks/useAlumniStories"
import { useSimilarSelectivitySchools } from "../data/useSimilarSelectivitySchools"
import { useSimilarSizeLocationSchools } from "../data/useSimilarSizeLocationSchools"

import LoadingSpinner from "../components/loading/LoadingSpinner"
import { faLocationDot } from "@fortawesome/pro-light-svg-icons"
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons"
import { faFacebook } from "@fortawesome/free-brands-svg-icons"
import { faXTwitter } from "@fortawesome/free-brands-svg-icons"
import { faYoutube } from "@fortawesome/free-brands-svg-icons"
import { faLinkSimple } from "@fortawesome/pro-light-svg-icons"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons"
import LanguageSwitcher from "../components/language-switcher/LanguageSwitcher"
import questionData from "../components/assessment/questions.js"
import AccountDropdown from "../components/account/AccountDropdown.js"

// Wingspans UI Components
import {
  WingsHero,
  WingsEyebrow,
  WingsHeading,
  WingsDetailText,
  WingsBadge,
  WingsViewMore,
  WingsSearchBar,
  WingsText,
  WingsLink,
  WingsStoryCard,
} from "../global-styles/wingspans-ui/components"
import "../global-styles/wingspans-ui/index.css"
import SimpleLocalize from "../components/SimpleLocalize/SimpleLocalize"

import "./school.css"

const ALUMNI_CARDS_PER_PAGE = 3

/**
 * School template component (legacy compatibility).
 * 
 * This component is maintained for compatibility with existing static pages
 * that may still use gatsby-node page creation. For new client-side routes,
 * see /src/pages/school/[slug].js instead.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Gatsby GraphQL data (legacy, not used with client-side fetching)
 * @param {Object} props.pageContext - Page context from gatsby-node
 * @param {string} props.pageContext.slug - School slug
 * @param {string} props.pageContext.language - Locale language code
 * @returns {JSX.Element} School template component
 */
export default function SchoolTemplate({ data, pageContext }) {
  const slug = pageContext?.slug
  const language = pageContext?.language || 'en-US'

  const { school, loading, error } = useCosmicSchool(slug, language)

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || !school) {
    return <div>Error loading school data</div>
  }

  return <SchoolContent cosmicData={school} data={data} pageContext={pageContext} forProspectiveStudent={false} />
}

/**
 * School content component that renders the complete school page.
 * 
 * Displays school information including overview, admissions data, majors/degrees,
 * tuition information, student body statistics, athletics, and related stories.
 * Integrates with Firebase for Peterson's data and user authentication.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.cosmicData - School data from Cosmic CMS
 * @param {Object} props.data - Additional GraphQL data (optional)
 * @param {Object} props.pageContext - Page context with language and routing info
 * @param {boolean} props.forProspectiveStudent - Whether this is a prospective student preview
 * @param {string} props.schCode - School code for prospective student mode
 * @param {Object[]} props.studentStories - Array of student stories to display on prospective student landing page
 * @returns {JSX.Element} School content component
 */
export function SchoolContent({ cosmicData, data, pageContext, forProspectiveStudent, schCode, studentStories }) {
  /**
   * Contains all school data from Cosmic CMS.
   */
  const post = cosmicData

  const [prospectiveStudentPageEdits, setProspectiveStudentPageEdits] = useState(null)
  const [isTaleTaken, setIsTaleTaken] = useState(false)

  const { user, profile, loading, firebase } = useContext(FirebaseContext)
  
  // Pulling enrolled and pid from post metadata (Cosmic data)
  const schoolPid = post?.metadata?.school_pid
  
  // Fetch Peterson's school data from SQL database
  const { schoolData, majorsData, programData, loading: loadingPetersons } = useSQLPetersonsSchool(schoolPid, firebase)
  const [selectedTab, setSelectedTab] = useState("majors")

  const { formatMessage } = useIntl()
  const { locale } = useIntl()
  const schoolContact = post?.metadata?.school_contact_url
  const schoolAdmissions = post?.metadata?.school_admissions_url
  const schoolGoogleMap = post?.metadata?.school_google_map
  const schoolFacebook = post?.metadata?.school_social_facebook
  const schoolTwitter = post?.metadata?.school_social_twitter
  const schoolYoutube = post?.metadata?.school_social_youtube
  const schoolEnrolled = post?.metadata?.school_enrolled_count

  useEffect(() => {
    const fetchEdits = async () => {
      const edits = await firebase.getProspectiveStudentPageEdits(schCode)
      setProspectiveStudentPageEdits(edits || {})
    }
    if (schCode && firebase) {
      fetchEdits()
    }
  }, [schCode, firebase])

  useEffect(() => {
    if (post?.slug && post?.title && firebase) {
      firebase.logSchoolPageVisit(post.slug, post.title)
    }
  }, [post, post?.slug, post?.title, firebase])

  useEffect(() => {
    if (!firebase) return
    const fetchSavedResponses = async () => {
      const savedResponses = await firebase.getUserTaleResponsesAndMatches()
      const { data } = savedResponses || {}
      const { taleResponses } = data || {}

      if (taleResponses) {
        const { isSubmitted } = taleResponses
        if (isSubmitted === true) {
          setIsTaleTaken(true)
        } else if (isSubmitted === false) {
          setIsTaleTaken(false)
        }
      }
    }
    fetchSavedResponses()
  }, [firebase])

  // check if we should load the people tab
  const alumniStories = useAlumniStories(post.slug)

  // if not prospective student page, use pagination component to navigate alumni stories
  const alumniSectionRef = useRef(null)
  const activeStoryBookmarks = locale === "es-ES" ? profile?.bookmarksEs ?? profile?.bookmarks : profile?.bookmarks

  // if on prospective student tool, alumni stories can be searched
  const [alumniFuseQuery, setAlumniFuseQuery] = useState("")
  const alumniFuseIndex = new Fuse(alumniStories, {
    keys: ["title", "metadata.story_job_title", "metadata.story_location"],
    includeScore: true,
    minMatchCharLength: 2,
  })
  const searchedAlumni = alumniFuseQuery.length < 2
    ? alumniStories
    : alumniFuseIndex.search(alumniFuseQuery).map(result => result.item)
  const displayedAlumniCards = searchedAlumni.map(
    story => cosmicStoryToStoryCardContent(story)
  )

  const similarSelectivitySchools = useSimilarSelectivitySchools(post.slug)
  const similarSizeLocationSchools = useSimilarSizeLocationSchools(post.slug)

  // hold up rendering until we're loaded.
  if (!user && !pageContext?.allowedPageSlugs?.includes(post?.slug)) {
    if (loading) {
      return <></>
    } else {
      navigate("/403")
      return null
    }
  }

  if (loadingPetersons) {
    return <LoadingSpinner />
  }

  const majorsTabFields = ["majors"]

  const athleticTabFields = ["athletics"]

  const peopleTabFields = ["people"]

  const studentBodyFields = [
    "percentInStateStudents",
    "percentOutOfStateStudents",
    "percentInternationalStudents",
    "percentMaleStudents",
    "percentFemaleStudents",
    "demographicNativeAmerican",
    "demographicAsian",
    "demographicBlack",
    "demographicLatin",
    "demographicPacificIslander",
    "demographicOther",
    "demographicMixed",
    "demographicWhite",
  ]

  const tuitionFields = [
    "averageFinancialAidCoverage",
    "generalTuition",
    "outOfStateTuition",
    "inStateTuition",
    "costBooksAndSuppliesCost",
    "costRoomAndBoard",
    "costRoomOnly",
    "averageStudentNeedsMet",
    "averageFinancialAid",
    "averageLoanAndWorkStudy",
    "averageScholarship",
  ]

  const admissionsFields = [
    "acceptanceRate",
    "appliedCount",
    "acceptedCount",
    "averageSatReading",
    "averageSatReading25th",
    "averageSatReading75th",
    "actCompositeScore",
    "actCompositeScore25th",
    "actCompositeScore75th",
    "applicationFee",
    "averageHighSchoolGPA",
    "requirements",
    "applicationDeadlineFreshman",
    "applicationDeadlineEarly",
    "applicationDeadlineTransfer",
  ]

  const tabFieldMap = {
    majors: majorsTabFields,
    admissions: admissionsFields,
    tuition: tuitionFields,
    studentBody: studentBodyFields,
    athletics: athleticTabFields,
    people: peopleTabFields,
  }

  /* Set startTabs conditionally based on the type of school so we only have to
   * render the applicable tabs */
  const startTabs = []

  if (schoolData) {
    if (majorsData.length > 0) {
      startTabs.push({
        key: "majors",
        name: formatMessage({ id: "majors-and-degrees", defaultMessage: "Majors & Degrees" }),
        content: <SectionMajorsDegrees schoolData={schoolData} majorsData={majorsData} programData={programData} />,
        //cssClassName: "majors-and-degrees",
      })
    }

    if (schoolData?.ugSchoolType) {
      startTabs.push(
        {
          key: "admissions",
          name: formatMessage({ id: "admissions", defaultMessage: "Admissions" }),
          content: <SectionAdmissions schoolData={schoolData} />,
          //cssClassName: "admissions",
        },
        {
          key: "tuition",
          name: formatMessage({ id: "tuition-and-aid", defaultMessage: "Tuition & Aid" }),
          content: <SectionTuitionAid schoolData={schoolData} />,
          //cssClassName: "tuition-and-aid",
        },
      )
    }

    if (
      schoolData?.percentFemaleStudents ||
      schoolData?.totalUndergradStudents ||
      schoolData?.percentMaleStudents ||
      schoolData?.demographicNativeAmerican ||
      schoolData?.demographicAsian ||
      schoolData?.demographicBlack ||
      schoolData?.demographicLatin ||
      schoolData?.demographicPacificIslander ||
      schoolData?.demographicOther ||
      schoolData?.demographicMixed ||
      schoolData?.demographicWhite
    ) {
      startTabs.push({
        key: "studentBody",
        name: formatMessage({ id: "student-body", defaultMessage: "Student Body" }),
        content: <SectionStudentBody schoolData={schoolData} />,
        //cssClassName: "student-body",
      })
    }

    if (schoolData?.ugSchoolType && schoolData?.athletics?.length > 0) {
      startTabs.push({
        key: "athletics",
        name: formatMessage({ id: "athletics", defaultMessage: "Athletics" }),
        content: <SectionAthletics schoolData={schoolData} />,
        //cssClassName: "athletics",
      })
    }

    // /* If there are any stories that reference this school, then we should render
    //  * the people tab */
    // if (schoolHasPeopleTab && !forProspectiveStudent) {
    //   startTabs.push({
    //     key: "people",
    //     name: formatMessage({ id: "algolia-stories-view-the-people", defaultMessage: "The People" }),
    //     content: <AlgoliaStories title={post?.title} classContext={"schools"} filterReference={"story_school_reference"} />,
    //     cssClassName: "people",
    //   })
    // }
  }

  /* Filter out any tabs that do not have enough data so we only render tabs
   * that are useful. If the people tab is in startTabs, then don't filter it
   * out -- we already checked whether we have people data when determining
   * the value of schoolHasPeopleTab.
   */
  const fieldExists = (fieldName) => {
    return schoolData !== null && schoolData[fieldName] !== null && schoolData[fieldName] !== undefined
  }

  const tabs = startTabs.filter((tab) => {
    return (
      tabFieldMap[tab.key] ||
      // .some(fieldExists)
      tab.key === "people"
    )
  })

  const activeProspectiveTabKey =
    startTabs.some((tab) => tab.key === selectedTab)
      ? selectedTab
      : startTabs[0]?.key

  const activeSchoolTabKey =
    tabs.some((tab) => tab.key === selectedTab)
      ? selectedTab
      : tabs[0]?.key

  const createTrackedLink = (url, linkText, linkSlug) => {
    const clickTracker = () => {
      firebase?.logSchoolLinkClick(post?.slug, linkSlug)
      return true
    }
    return (
      <a className="school-list-contact-item-social" target="_blank" rel="noreferrer" href={url} onClick={clickTracker} onContextMenu={clickTracker}>
        {linkText}{" "}
      </a>
    )
  }

  const studentStoryCards = studentStories?.map(
    story => cosmicStudentStoryToStoryCardContent(story)
  ) || []

  if (!post || !profile || loadingPetersons || (schCode && !prospectiveStudentPageEdits)) {
    return <LoadingSpinner />
  }

  const prospectiveEdits = prospectiveStudentPageEdits || {}
  let validStats = []
  const {
    inStateTuition,
    outOfStateTuition,
    generalTuition,
    averageFinancialAidCoverage,
    enrolledCount,
    facultySize,
    acceptanceRate,
    studentsLivingInHousing,
    diversityNonWhite,
    ratioStudentFaculty,
  } = schoolData || {}

  if (acceptanceRate && acceptanceRate !== "NaN") {
    validStats.push({
      name: formatMessage({ id: "prospective-acceptance-rate", defaultMessage: "acceptance rate" }),
      value: `${Math.round(acceptanceRate)}%`,
    })
  }
  if (inStateTuition) {
    validStats.push({
      name: formatMessage({ id: "prospective-per-year-in-state", defaultMessage: "per year, in-state" }),
      value: `$${inStateTuition?.value?.toLocaleString()}`,
    })
  }
  if (outOfStateTuition) {
    validStats.push({
      name: formatMessage({ id: "prospective-per-year-out-state", defaultMessage: "per year, out-of-state" }),
      value: `$${outOfStateTuition?.value?.toLocaleString()}`,
    })
  }
  if (enrolledCount) {
    validStats.push({
      name: formatMessage({ id: "prospective-total-students", defaultMessage: "total students" }),
      value: `${enrolledCount?.toLocaleString()}`,
    })
  }
  if (ratioStudentFaculty) {
    validStats.push({
      name: formatMessage({ id: "prospective-student-faculty-ratio", defaultMessage: "student:faculty ratio" }),
      value: `${ratioStudentFaculty}:1`,
    })
  }
  if (diversityNonWhite) {
    validStats.push({
      name: formatMessage({ id: "prospective-non-white-students", defaultMessage: "non-white students" }),
      value: `${Math.floor(diversityNonWhite)}%`,
    })
  }
  if (averageFinancialAidCoverage && averageFinancialAidCoverage !== "0.00") {
    validStats.push({
      name: formatMessage({ id: "prospective-avg-financial-aid", defaultMessage: "avg financial aid" }),
      value: `${averageFinancialAidCoverage}%`,
    })
  }
  if (studentsLivingInHousing && studentsLivingInHousing !== "0.00") {
    validStats.push({
      name: formatMessage({ id: "prospective-students-in-housing", defaultMessage: "students in housing" }),
      value: `${studentsLivingInHousing}%`,
    })
  }
  if (generalTuition) {
    validStats.push({
      name: formatMessage({ id: "prospective-students-average-tuition", defaultMessage: "average tuition" }),
      value: `$${generalTuition?.value?.toLocaleString()}`,
    })
  }
  if (prospectiveEdits?.stats && prospectiveEdits?.stats?.length > 0) {
    validStats = prospectiveEdits.stats.map((stat) => {
      return {
        name: locale === "es-ES" && stat?.nameEs ? stat.nameEs : stat?.name,
        value: locale === "es-ES" && stat?.valueEs ? stat.valueEs : stat?.value,
      }
    })
  }

  const orderedValidStats = [null, null, null, null, null, null]
  if (validStats.length >= 1) {
    orderedValidStats[2] = validStats[0]
  }
  if (validStats.length >= 2) {
    orderedValidStats[0] = validStats[1]
  }
  if (validStats.length == 3) {
    orderedValidStats[3] = validStats[2]
  } else if (validStats.length > 3) {
    orderedValidStats[1] = validStats[2]
  }
  if (validStats.length >= 4) {
    orderedValidStats[3] = validStats[3]
  }
  if (validStats.length >= 5) {
    orderedValidStats[4] = validStats[4]
  }
  if (validStats.length >= 6) {
    orderedValidStats[5] = validStats[5]
  }

  const bannerImageUrl =
    prospectiveEdits?.bannerImageUrl ||
    post?.metadata?.school_banner_image?.imgix_url ||
    "https://imgix.cosmicjs.com/8cbd9150-6568-11eb-8120-dfe8ec2b682f-campus-centered.jpg"
  const schoolOverview = (locale === "es-ES" ? prospectiveEdits?.overviewEs : prospectiveEdits?.overview) || post?.metadata?.school_overview || ""
  const quickLinks = prospectiveEdits?.quickLinks || []

  const displayStat = (stat) => {
    if (!stat) {
      return null
    } else {
      return (
        <div className="statistic" key={stat.name}>
          <div className="stat-value">{stat.value}</div>
          <div className="stat-name">{stat.name}</div>
        </div>
      )
    }
  }

  const completedQuiz = !!profile?.assessmentRecord?.resultProfile
  const canResumeQuiz =
    !!profile?.assessmentRecord &&
    profile?.assessmentRecord?.responsesVersion === questionData?.version &&
    profile?.assessmentRecord?.responses?.length < questionData?.questions?.length

  if (forProspectiveStudent) {
    return (
      <>
        <div
          className="prospective-landing-page-banner-image"
          style={{
            backgroundImage: `linear-gradient(0.11deg, rgba(54, 54, 54, 0.28) 82.93%, rgba(54, 54, 54, 0.62) 93.74%), url(${bannerImageUrl})`,
          }}
        >
          <nav className="prospective-landing-page-header">
            <LanguageSwitcher top={true} className="prospective-student-language-switcher" />
            <AccountDropdown />
          </nav>
        </div>

        <section className="school-container">
          <SEO title={`${post?.title} | Wingspans`} image={bannerImageUrl} />
          <div className="location">
            <FontAwesomeIcon icon={faLocationDot} color="#CAFE13" />
            &nbsp;
            {`${post?.metadata?.school_city}${post?.metadata?.school_city && post?.metadata?.school_state_or_region ? ", " : ""}${
              post?.metadata?.school_state_or_region
            }`.toUpperCase()}
          </div>
          <div className="welcome-box">
            {forProspectiveStudent && (
              <>
                <div className="welcome-message">
                  <FormattedMessage id="prospective-hi" defaultMessage="Hi" />
                  {`, ${profile?.firstName || profile?.email?.split("@")?.[0]}!`}
                </div>
                <div className="dividing-line" />
              </>
            )}
            <div className="logged-in-with">
              <FormattedMessage id="prospective-youre-logged-in-with" defaultMessage="you're logged in with" />
            </div>
            <div className="school-name">{post?.title}</div>
            {schoolOverview && (
              <div className="school-overview">
                <WingsViewMore html={schoolOverview} length={250} />
              </div>
            )}
            <div className="welcome-buttons-container">
              {!forProspectiveStudent && schoolContact?.url && (
                <a className="blue-link" href={schoolContact.url}>
                  <FontAwesomeIcon icon={faExternalLink} />
                  &nbsp;
                  <FormattedMessage id="prospective-visit-the-website" defaultMessage="Visit the Website" />
                </a>
              )}
              {forProspectiveStudent && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center" }}>
                  {completedQuiz ? (
                    <a className="blue-link" href="/assessment/personality/results">
                      <FormattedMessage id="prospective-personality-quiz-results" defaultMessage="Interest Profiler Results" />
                    </a>
                  ) : (
                    <a className="blue-link" href="/assessment/personality/">
                      {canResumeQuiz ? (
                        <FormattedMessage id="prospective-resume-personality-quiz" defaultMessage="Resume Interest Profiler" />
                      ) : (
                        <FormattedMessage id="prospective-personality-quiz" defaultMessage="Interest Profiler" />
                      )}
                    </a>
                  )}

                  {isTaleTaken ? (
                    <a className="pink-link" href="/tal-e/?page=results">
                      <FormattedMessage id="prospective-tale-results" defaultMessage="Experience Translator Results" />
                    </a>
                  ) : (
                    <a className="pink-link" href="/tal-e">
                      <FormattedMessage id="prospective-tale-experience-translator" defaultMessage="Experience Translator" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
         {alumniStories.length > 0 && (
            <div className="w-full alumni-stories-section">
              <div className="w-container w-flex w-flex-column w-items-end w-mb-2">
                <WingsSearchBar
                  className="prospective-student-alumni-stories-searchbar w-mb-0"
                  placeholder={formatMessage({ id: "alumni-search-placeholder", defaultMessage: "Search by name, job title, location" })}
                  value={alumniFuseQuery}
                  onChange={setAlumniFuseQuery}
                />
              </div>
              <StoryCardCarousel storyCards={displayedAlumniCards} />
            </div>
          )}
          {schoolAdmissions?.url && (
            <a className="blue-link" target="_blank" href={schoolAdmissions?.url}>
              <FormattedMessage id="prospective-apply-now" defaultMessage="Apply Now" />
            </a>
          )}
          {validStats.length > 0 && (
            <div className="stats-section">
              {validStats.length > 1 && <div className="white-stat-box">{orderedValidStats.slice(0, 2).map((stat) => displayStat(stat))}</div>}
              <div className="pink-stat-box">{displayStat(orderedValidStats[2])}</div>
              {validStats.length > 2 && <div className="white-stat-box">{orderedValidStats.slice(3).map((stat) => displayStat(stat))}</div>}
            </div>
          )}
          <div className="tabs-container shared-tab-styles">
            <div className="tab-options-container">
              {startTabs.map((tab) => (
                <button
                  className={`tab-option ${activeProspectiveTabKey === tab.key ? tab.key : "unselected"}`}
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key)}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            {startTabs.find((t) => t.key === activeProspectiveTabKey)?.content}
          </div>
          {quickLinks.length > 0 && (
            <div className="quick-links-section">
              <div className="quick-links-title">
                <FormattedMessage id="prospective-quick-links" defaultMessage="quick links" />
              </div>
              <div className="quick-links-container">
                {quickLinks.map((link) => (
                  <div className="quick-link">
                    <div className="link-icon">
                      <FontAwesomeIcon icon={faLinkSimple} />
                    </div>
                    <div className="link-text-container">
                      {locale === "es-ES" ? link?.descriptionEs : link?.description}
                      <a href={link?.url} target="_blank">
                        {(locale === "es-ES" ? link?.nameEs : link?.name) || link?.url}
                        &nbsp;
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {studentStoryCards.length > 0 && (
            <div className="student-stories-section">
              <div className="student-stories-title">
                <FormattedMessage id="prospective-meet-the-students" defaultMessage="Meet the Students" />
              </div>
              <StoryCardCarousel storyCards={studentStoryCards} />
            </div>
          )}
          <div className="footer-section">
            <div className="footer-link-buttons">
              {forProspectiveStudent ? (
                <>
                  {schoolAdmissions?.url && (
                    <a className="blue-link" target="_blank" href={schoolAdmissions?.url}>
                      <FormattedMessage id="prospective-apply-now" defaultMessage="Apply Now" />
                    </a>
                  )}
                  {/* <a className="pink-link">Experience Translator</a> */}
                </>
              ) : (
                <>
                  {schoolAdmissions?.url && (
                    <a className="blue-link" target="_blank" href={schoolAdmissions?.url}>
                      <FormattedMessage id="prospective-admissions" defaultMessage="Admissions" />
                    </a>
                  )}
                  {schoolContact?.url && (
                    <a className="blue-link" href={schoolContact.url}>
                      <FontAwesomeIcon icon={faExternalLink} />
                      &nbsp;
                      <FormattedMessage id="prospective-visit-the-webiste" defaultMessage="Visit the Website" />
                      Visit the Website
                    </a>
                  )}
                </>
              )}
            </div>
            {((schoolContact && schoolContact.url) ||
              (schoolAdmissions && schoolAdmissions.url) ||
              (schoolGoogleMap && schoolGoogleMap.url) ||
              (schoolFacebook && schoolFacebook.url) ||
              (schoolTwitter && schoolTwitter.url) ||
              (schoolYoutube && schoolYoutube.url)) && (
              <div className="contact-info">
                <div className="contact-your-school">
                  <FormattedMessage id="prospective-contact-your-school" defaultMessage="contact your school" />
                </div>
                {schoolContact?.url && createTrackedLink(schoolContact.url, schoolContact?.text || schoolContact?.url, "website")}
                {schoolAdmissions?.url && createTrackedLink(schoolAdmissions.url, schoolAdmissions?.text || schoolAdmissions?.url, "admissions")}
                {schoolGoogleMap?.url && createTrackedLink(schoolGoogleMap?.url, schoolGoogleMap?.text || schoolGoogleMap?.url, "gooogle-map")}

                {(schoolFacebook?.url || schoolTwitter?.url || schoolYoutube?.url) && (
                  <div className="school-socials">
                    {schoolFacebook?.url &&
                      createTrackedLink(schoolFacebook.url, <FontAwesomeIcon icon={faFacebook} size="2x" color="#363636" />, "facebook")}
                    {schoolTwitter?.url &&
                      createTrackedLink(schoolTwitter.url, <FontAwesomeIcon icon={faXTwitter} size="2x" color="#363636" />, "twitter")}
                    {schoolYoutube?.url &&
                      createTrackedLink(schoolYoutube.url, <FontAwesomeIcon icon={faYoutube} size="2x" color="#363636" />, "youtube")}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </>
    )
  }

  /*
   * Build grouped stat pills for the new school header
   * (Tuition+Aid, Selectivity, Population, Faculty, Diversity)
   */
  const statGroups = []

  const tuitionItems = []
  if (schoolData?.inStateTuition?.value) {
    tuitionItems.push({
      label: <FormattedMessage id="school-stat-in-state" defaultMessage="In-State" />,
      value: `$${schoolData.inStateTuition.value.toLocaleString("en")}/${
        !schoolData?.inStateTuition?.unit || schoolData?.inStateTuition?.unit === "year" ? "Year" : schoolData?.inStateTuition?.unit
      }`,
    })
  }
  if (schoolData?.outOfStateTuition?.value) {
    tuitionItems.push({
      label: <FormattedMessage id="school-stat-out-of-state" defaultMessage="Out of State" />,
      value: `$${schoolData.outOfStateTuition.value.toLocaleString("en")}/${
        !schoolData?.outOfStateTuition?.unit || schoolData?.outOfStateTuition?.unit === "year" ? "Year" : schoolData?.outOfStateTuition?.unit
      }`,
    })
  }
  if (!schoolData?.inStateTuition && !schoolData?.outOfStateTuition && schoolData?.generalTuition?.value) {
    tuitionItems.push({
      label: <FormattedMessage id="school-stat-tuition" defaultMessage="Tuition" />,
      value: `$${schoolData.generalTuition.value.toLocaleString("en")}/Year`,
    })
  }
  if (tuitionItems.length > 0) {
    statGroups.push({
      key: "tuition",
      label: <FormattedMessage id="school-stat-tuition-aid" defaultMessage="Tuition + Aid" />,
      items: tuitionItems,
    })
  }

  if (schoolData?.acceptanceRate && !isNaN(schoolData?.acceptanceRate)) {
    statGroups.push({
      key: "selectivity",
      label: <FormattedMessage id="school-stat-selectivity" defaultMessage="Selectivity" />,
      items: [
        {
          label: <FormattedMessage id="school-stat-acceptance-rate" defaultMessage="Acceptance Rate" />,
          value: `${Math.round(schoolData.acceptanceRate)}%`,
        },
      ],
    })
  }

  if (schoolData?.enrolledCount) {
    statGroups.push({
      key: "population",
      label: <FormattedMessage id="school-stat-population" defaultMessage="Population" />,
      items: [
        {
          label: schoolData?.sizeFilter ? (
            <FormattedMessage id="school-stat-size-school" defaultMessage="{size} Size School" values={{ size: schoolData.sizeFilter }} />
          ) : (
            <FormattedMessage id="school-stat-students" defaultMessage="Students" />
          ),
          value: schoolData.enrolledCount.toLocaleString("en"),
        },
      ],
    })
  }

  const facultyItems = []
  if (schoolData?.facultySize) {
    facultyItems.push({
      label: <FormattedMessage id="school-stat-faculty-label" defaultMessage="Faculty" />,
      value: schoolData.facultySize.toLocaleString("en"),
    })
  }
  if (schoolData?.ratioStudentFaculty) {
    facultyItems.push({
      label: <FormattedMessage id="school-stat-student-faculty-ratio" defaultMessage="Student/Faculty Ratio" />,
      value: `${schoolData.ratioStudentFaculty}:1`,
    })
  }
  if (facultyItems.length > 0) {
    statGroups.push({
      key: "faculty",
      label: <FormattedMessage id="school-stat-faculty" defaultMessage="Faculty" />,
      items: facultyItems,
    })
  }

  if (schoolData?.diversityNonWhite) {
    statGroups.push({
      key: "diversity",
      label: <FormattedMessage id="school-stat-diversity" defaultMessage="Diversity" />,
      items: [
        {
          label: <FormattedMessage id="school-stat-non-white" defaultMessage="Non-White" />,
          value: `${parseFloat(schoolData.diversityNonWhite).toFixed(2)}%`,
        },
      ],
    })
  }

  /*
   * Setting & location label for the bar below the hero
   */
  const settingParts = []
  if (post?.metadata?.school_city) settingParts.push(post.metadata.school_city)
  if (post?.metadata?.school_state_or_region) settingParts.push(post.metadata.school_state_or_region)
  if (post?.metadata?.school_country) settingParts.push(post.metadata.school_country)
  const locationLabel = settingParts.join(", ")
  const settingLabel = post?.metadata?.school_setting_filter

  const hasContactInfo =
    !!(schoolContact?.url || schoolAdmissions?.url || schoolGoogleMap?.url || schoolFacebook?.url || schoolTwitter?.url || schoolYoutube?.url)

  return (
    <SimpleLocalize {...{ pageContext }}>
      <SEO
        title={`${post?.title} | Wingspans`}
        image={bannerImageUrl}
      />

      <div className="school-page w-flex w-flex-column">
        {/* HERO IMAGE */}
        <WingsHero
          imageSrc={bannerImageUrl}
          imageAlt={
            post?.metadata?.school_banner_image_alt ||
            formatMessage({ id: "banner-image-for-school", defaultMessage: "Banner image for {name}" }, { name: post?.title })
          }
          overlayOpacity="none"
          textAlign="center"
          alignItems="end"
        />

        {/* SETTING / LOCATION BAR */}
        {(settingLabel || locationLabel) && (
          <div className="school-setting-bar w-flex w-items-center w-justify-center w-text-center w-py-1">
            <WingsDetailText>
              {settingLabel && (
                <>
                  <b>{settingLabel} <FormattedMessage id="setting" defaultMessage="setting" />:</b>
                  &nbsp;
                </>
              )}
              {locationLabel}
            </WingsDetailText>
          </div>
        )}

        {/* SCHOOL HEADER (eyebrow + title + save bookmark) */}
        <section className="school-header w-py-3 w-container w-flex w-flex-column w-items-center w-text-center">
          <WingsEyebrow className="w-mb-1">
            <FormattedMessage id="school-schools-eyebrow" defaultMessage="Schools" />
          </WingsEyebrow>
          <WingsHeading level={1} className="w-mb-2">
            {post?.title}
          </WingsHeading>
          {user && (
            <Bookmark
              contentTitle={post?.title}
              label={<FormattedMessage id="save-school" defaultMessage="Save School" />}
              classContext="school"
              contentId={post?.id}
              contentSlug={post?.slug}
              contentType={post?.type}
            />
          )}
        </section>

        {/* STAT GROUP PILLS */}
        {statGroups.length > 0 && (
          <section className="school-stats w-pb-3 w-container w-flex w-flex-wrap w-justify-center w-items-start w-gap-3">
            {statGroups.map((group) => (
              <div key={group.key} className="school-stats-group w-flex w-flex-column w-items-center">
                <WingsBadge variant="primary" className="w-mb-1">
                  {group.label}
                </WingsBadge>
                {group.items.map((item, idx) => (
                  <WingsDetailText key={idx} as="div" className="school-stats-item w-text-center">
                    {item.value && <strong>{item.value}</strong>}
                    {item.value && item.label ? " " : ""}
                    {item.label}
                  </WingsDetailText>
                ))}
              </div>
            ))}
          </section>
        )}

        {/* OVERVIEW + CONTACT GRID */}
        {(post?.metadata?.school_overview || hasContactInfo) && (
          <section className="school-overview-section w-py-3 w-container">
            <div className="school-overview-grid w-flex w-flex-column w-gap-3">
              {post?.metadata?.school_overview && (
                <div className="school-overview-text">
                  <WingsEyebrow className="w-mb-2">
                    <FormattedMessage id="overview" defaultMessage="Overview" />
                  </WingsEyebrow>
                  <WingsViewMore html={post.metadata.school_overview} length={600} />
                </div>
              )}

              {hasContactInfo && (
                <aside className="school-contact-card w-flex w-flex-column w-p-2">
                  <WingsEyebrow className="w-mb-2">
                    <FormattedMessage
                      id="school-contact-name"
                      defaultMessage="Contact {name}"
                      values={{ name: post?.title }}
                    />
                  </WingsEyebrow>

                  {schoolContact?.url && (
                    <WingsDetailText as="div" className="school-contact-link w-mb-1">
                      {createTrackedLink(
                        schoolContact.url,
                        schoolContact?.text || schoolContact?.url,
                        "website"
                      )}
                    </WingsDetailText>
                  )}
                  {schoolAdmissions?.url && (
                    <WingsDetailText as="div" className="school-contact-link w-mb-1">
                      {createTrackedLink(
                        schoolAdmissions.url,
                        schoolAdmissions?.text || schoolAdmissions?.url,
                        "admissions"
                      )}
                    </WingsDetailText>
                  )}
                  {schoolGoogleMap?.url && (
                    <WingsDetailText as="div" className="school-contact-link w-mb-1">
                      {createTrackedLink(
                        schoolGoogleMap.url,
                        schoolGoogleMap?.text || schoolGoogleMap?.url,
                        "gooogle-map"
                      )}
                    </WingsDetailText>
                  )}

                  {(schoolFacebook?.url || schoolTwitter?.url || schoolYoutube?.url) && (
                    <div className="school-contact-socials w-flex w-items-center w-gap-2 w-mt-1">
                      {schoolFacebook?.url &&
                        createTrackedLink(
                          schoolFacebook.url,
                          <FontAwesomeIcon icon={faFacebook} size="lg" color="#363636" />,
                          "facebook"
                        )}
                      {schoolTwitter?.url &&
                        createTrackedLink(
                          schoolTwitter.url,
                          <FontAwesomeIcon icon={faXTwitter} size="lg" color="#363636" />,
                          "twitter"
                        )}
                      {schoolYoutube?.url &&
                        createTrackedLink(
                          schoolYoutube.url,
                          <FontAwesomeIcon icon={faYoutube} size="lg" color="#363636" />,
                          "youtube"
                        )}
                    </div>
                  )}
                </aside>
              )}
            </div>
          </section>
        )}

        {/* THE ALUMNI / STORIES */}
        {alumniStories.length > 0 && (
          <FilterableCardGrid
            items={alumniStories}
            sectionClassName="w-container w-py-3"
            heading={<FormattedMessage id="school-the-alumni" defaultMessage="The Alumni" />}
            headingColor="teal"
            sortByLabel={<FormattedMessage id="algolia-stories-view-sort-by" defaultMessage="Sort by:" />}
            newestLabel={<FormattedMessage id="algolia-stories-view-newest" defaultMessage="Newest" />}
            savedLabel={<FormattedMessage id="algolia-stories-view-saved" defaultMessage="Saved" />}
            searchPlaceholder={formatMessage({ id: "search-by-n-j-t-l-c", defaultMessage: "Search by name, job title, location, company" })}
            getSearchValues={(story) => [
              story?.title,
              story?.metadata?.story_job_title,
              story?.metadata?.story_location,
              story?.metadata?.story_company,
            ]}
            savedFilter={(story) => Boolean(activeStoryBookmarks?.[story.slug])}
            itemsPerPage={ALUMNI_CARDS_PER_PAGE}
            getItemKey={(story) => `${story.slug}-${story.locale}` || story.id}
            scrollRef={alumniSectionRef}
            cardsContainerClassName="w-default-cards-container w-full"
            renderItemCard={(story) => (
              <WingsStoryCard
                img={story.metadata.story_personal_photo?.imgix_url}
                video={story.metadata.story_video_url}
                objectID={`${story.slug}-${story.locale}`}
                title={story.title}
                storyCompany={story.metadata.story_company}
                storyQuote={story.metadata.story_quote}
                storyJobTitle={story.metadata.story_job_title}
                slug={story.slug}
                contentType={story.type}
              />
            )}
          />
        )}

        {/* TABS (Majors, Admissions, Tuition, Student Body, Athletics, People) */}
        {tabs.length > 0 && (
          <section className="school-tabs-section w-py-3 w-container shared-tab-styles">
            <div className="tab-options-container" role="tablist" aria-label={formatMessage({ id: "school-tabs", defaultMessage: "School tabs" })}>
              {tabs.map((tab) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSchoolTabKey === tab.key}
                  className={`tab-option ${activeSchoolTabKey === tab.key ? tab.key : "unselected"}`}
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key)}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            {tabs.find((tab) => tab.key === activeSchoolTabKey)?.content}
          </section>
        )}

        {/* MEET THE STUDENTS - shown when student stories data is supplied */}
        {studentStoryCards.length > 0 && (
          <section className="school-meet-students w-py-3">
            <div className="w-flex w-justify-center w-mb-2">
              <WingsEyebrow>
                <FormattedMessage id="prospective-meet-the-students" defaultMessage="Meet the Students" />
              </WingsEyebrow>
            </div>
            <StoryCardCarousel storyCards={studentStoryCards} />
          </section>
        )}

        {/* RELATED SCHOOLS */}
        {(!!similarSelectivitySchools.length || !!similarSizeLocationSchools.length) && (
          <section className="school-related-section w-py-3 w-container w-flex w-justify-center">
            <div className="school-related-columns w-container-4xl ">
              {similarSelectivitySchools.length > 0 && (
                <div className="school-related-column">
                  <WingsEyebrow className="w-mb-2">
                    <FormattedMessage id="school-similar-similar-by-selectivity" defaultMessage="Similar by Selectivity" />
                  </WingsEyebrow>
                  <WingsText className="w-full">
                    {similarSelectivitySchools.slice(0, 10).map((school, idx) => (
                      <React.Fragment key={school.slug}>
                        <WingsLink to={`/school/${school.slug}`} display="inline" size="h6">
                          {school.title}
                        </WingsLink>
                        {idx < 9 && idx < similarSelectivitySchools.length && ", "}
                      </React.Fragment>
                    ))}
                  </WingsText>
                </div>
              )}

              {similarSizeLocationSchools.length > 0 && (
                <div className="school-related-column">
                  <WingsEyebrow className="w-mb-2">
                    <FormattedMessage id="school-similar-similar-by-size-location" defaultMessage="Similar by Size & Location" />
                  </WingsEyebrow>
                  <WingsText className="w-full">
                    {similarSizeLocationSchools.slice(0, 10).map((school, idx) => (
                      <React.Fragment key={school.slug}>
                        <WingsLink to={`/school/${school.slug}`} display="inline" size="h6">
                          {school.title}
                        </WingsLink>
                        {idx < 9 && idx < similarSizeLocationSchools.length && ", "}
                      </React.Fragment>
                    ))}
                  </WingsText>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </SimpleLocalize>
  )
}

// GraphQL query removed - now using client-side Cosmic API fetching
// export const pageQuery = graphql`...`
